// src/components/core/FilterMenu.jsx
import React, { memo } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Divider,
  Button,
  alpha
} from '@mui/material';
import { FilterAlt as FilterIcon } from '@mui/icons-material';

/**
 * Unified filter menu component for filtering items by various criteria
 * 
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Function to handle filter changes
 * @param {Element} props.anchorEl - Element to anchor the menu to
 * @param {boolean} props.open - Whether the menu is open
 * @param {Function} props.onClose - Function to close the menu
 * @param {Array} props.filterConfig - Configuration for filter fields
 * @param {Function} props.onClearAllFilters - Function to clear all filters
 */
const FilterMenu = ({
  filters = {},
  onFilterChange,
  anchorEl,
  open,
  onClose,
  filterConfig = [],
  onClearAllFilters
}) => {
  const theme = useTheme();
  
  // Handle filter change
  const handleChange = (filterName, value) => {
    if (onFilterChange) {
      onFilterChange(filterName, value);
    }
  };
  
  // Check if there are any active filters
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== 'all');
  };
  
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 3,
        sx: { 
          width: 280, 
          maxHeight: 'calc(100% - 96px)',
          mt: 1,
          // borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Typography variant="subtitle1" sx={{ 
            display: 'flex', 
            alignItems: 'center',
            background: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent'
          }}>
            <FilterIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.secondary.main }} />
            Filters
          </Typography>
          
          {hasActiveFilters() && onClearAllFilters && (
            <Button 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                onClearAllFilters();
                onClose();
              }}
              sx={{
                color: theme.palette.secondary.main,
                '&:hover': {
                  color: theme.palette.secondary.light,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                },
              }}
            >
              Clear All
            </Button>
          )}
        </Box>
        
        <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.text.primary, 0.1) }} />
        
        {filterConfig.map((filter, index) => (
          <React.Fragment key={filter.name}>
            <Typography variant="subtitle2" gutterBottom sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1.5 
            }}>
              {filter.label}
            </Typography>
            
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <Select
                value={filters[filter.name] || 'all'}
                onChange={(e) => handleChange(filter.name, e.target.value)}
                displayEmpty
                renderValue={filter.renderSelectedValue}
                sx={{
                  borderRadius: theme.aghrba?.tokens?.borderRadius?.button || 30,
                  backgroundColor: theme.palette.background.light,
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  height: theme.aghrba?.tokens?.buttonHeight?.small || 48.5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.text.primary, 0.1),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 3px ${theme.palette.secondary.main}40`,
                  },
                  '& .MuiSelect-select': {
                    padding: '14px 20px',
                  }
                }}
              >
                <MenuItem value="all" sx={{ 
                  borderRadius: 8,
                  margin: '2px 4px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    transform: 'translateY(-1px)',
                  },
                }}>
                  {filter.allLabel || `All ${filter.label}`}
                </MenuItem>
                
                {filter.options.map(option => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    sx={{
                      borderRadius: 8,
                      margin: '2px 4px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                        transform: 'translateY(-1px)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                        color: theme.palette.secondary.main,
                        fontWeight: 500,
                      },
                    }}
                  >
                    {filter.renderOption ? filter.renderOption(option) : option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {index < filterConfig.length - 1 && (
              <Divider sx={{ mb: 3, borderColor: alpha(theme.palette.text.primary, 0.05) }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Menu>
  );
};

export default memo(FilterMenu);